// ==================================
// Componente LogoUpload
// ==================================

import { useState, useRef } from 'react';
import { veterinariasServicio } from '@/servicios';
import type { LogoInfo } from '@/tipos';
import { Spinner } from './Spinner';

interface LogoUploadProps {
  currentLogo?: LogoInfo;
  veterinariaId?: string;
  onUpload: (logo: LogoInfo) => void;
  onRemove?: () => void;
  disabled?: boolean;
}

export function LogoUpload({
  currentLogo,
  veterinariaId,
  onUpload,
  onRemove,
  disabled = false,
}: LogoUploadProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('Solo se permiten archivos JPG, PNG o WebP');
      return;
    }

    // Validar tamanio (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('El archivo no debe superar 5MB');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const logo = await veterinariasServicio.subirLogo(file, veterinariaId);
      onUpload(logo);
    } catch {
      setError('Error al subir el logo');
    } finally {
      setLoading(false);
      // Reset input
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-stone-700">Logo</label>
      <div className="flex items-center gap-4">
        {/* Preview */}
        <div
          onClick={() => !disabled && !loading && inputRef.current?.click()}
          className={`w-20 h-20 rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden transition-colors ${
            disabled
              ? 'border-stone-200 bg-stone-50 cursor-not-allowed'
              : 'border-stone-300 hover:border-primary-400 cursor-pointer bg-stone-50 hover:bg-primary-50'
          }`}
        >
          {loading ? (
            <Spinner size="sm" />
          ) : currentLogo?.url ? (
            <img
              src={currentLogo.url}
              alt="Logo"
              className="w-full h-full object-cover"
            />
          ) : (
            <svg
              className="w-8 h-8 text-stone-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          )}
        </div>

        <div className="flex-1">
          <p className="text-sm text-stone-500">
            {currentLogo ? currentLogo.nombre : 'Click para subir logo'}
          </p>
          <p className="text-xs text-stone-400">JPG, PNG o WebP. Max 5MB</p>
          {currentLogo && onRemove && !disabled && (
            <button
              type="button"
              onClick={onRemove}
              className="text-xs text-red-500 hover:text-red-700 mt-1"
            >
              Eliminar logo
            </button>
          )}
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || loading}
      />

      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
}

export default LogoUpload;
